import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const DEFAULT_LINK_COLORS = {
  link_color_none: "#94a3b8",
  link_color_one: "#f59e0b",
  link_color_both: "#22c55e",
};

const defaultForm = {
  name: "",
  description: "",
  latitude: "",
  longitude: "",
  emoji_wait: "⌛️",
  emoji_ready: "✅",
  status: "active",
  is_ready: false,
  linked_address_id: "",
  ...DEFAULT_LINK_COLORS,
  link_skip_none: false,
  link_skip_one: false,
  link_skip_both: false,
};

const TargetAddressDialog = ({
  open,
  onClose,
  onSubmit,
  initialData,
  addressOptions = [],
}) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        description: initialData.description || "",
        latitude: initialData.latitude ?? "",
        longitude: initialData.longitude ?? "",
        emoji_wait: initialData.emoji_wait || "⌛️",
        emoji_ready: initialData.emoji_ready || "✅",
        status: initialData.status || "active",
        is_ready: Boolean(initialData.is_ready),
        linked_address_id: initialData.linked_address_id || "",
        link_color_none: initialData.link_color_none || DEFAULT_LINK_COLORS.link_color_none,
        link_color_one: initialData.link_color_one || DEFAULT_LINK_COLORS.link_color_one,
        link_color_both: initialData.link_color_both || DEFAULT_LINK_COLORS.link_color_both,
        link_skip_none: Boolean(initialData.link_skip_none),
        link_skip_one: Boolean(initialData.link_skip_one),
        link_skip_both: Boolean(initialData.link_skip_both),
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
    setIsSaving(false);
  }, [initialData, open]);

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = "Название обязательно";
    }
    if (!form.latitude || !Number.isFinite(Number(form.latitude))) {
      nextErrors.latitude = "Укажите корректную широту";
    }
    if (!form.longitude || !Number.isFinite(Number(form.longitude))) {
      nextErrors.longitude = "Укажите корректную долготу";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      await onSubmit({
        ...form,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        linked_address_id: form.linked_address_id || null,
      });
      onClose();
    } catch (error) {
      setIsSaving(false);
      setErrors({ submit: error.message || "Не удалось сохранить адрес" });
    }
  };

  const colorValue = (value, fallback) => {
    const v = (value || '').trim();
    return /^#([0-9a-fA-F]{6})$/.test(v) ? v : fallback;
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{initialData ? "Редактировать адрес" : "Новый адрес"}</DialogTitle>
          <DialogDescription>
            Укажите координаты, описание и статус точки. После сохранения точка появится в списке выбранной группы.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Название</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <Label>Описание</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Адрес, телефон, комментарий"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Широта</Label>
              <Input
                value={form.latitude}
                onChange={(e) => setForm((prev) => ({ ...prev, latitude: e.target.value }))}
                placeholder="55.751244"
              />
              {errors.latitude && <p className="text-xs text-red-500 mt-1">{errors.latitude}</p>}
            </div>
            <div>
              <Label>Долгота</Label>
              <Input
                value={form.longitude}
                onChange={(e) => setForm((prev) => ({ ...prev, longitude: e.target.value }))}
                placeholder="37.618423"
              />
              {errors.longitude && <p className="text-xs text-red-500 mt-1">{errors.longitude}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Emoji ожидания</Label>
              <Input
                value={form.emoji_wait}
                onChange={(e) => setForm((prev) => ({ ...prev, emoji_wait: e.target.value }))}
              />
            </div>
            <div>
              <Label>Emoji готовности</Label>
              <Input
                value={form.emoji_ready}
                onChange={(e) => setForm((prev) => ({ ...prev, emoji_ready: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label>Связанный адрес</Label>
              <Select
                value={form.linked_address_id || "none"}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    linked_address_id: value === "none" ? "" : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите адрес (опционально)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Нет связи</SelectItem>
                  {addressOptions
                    .filter((addr) => addr.id !== initialData?.id)
                    .map((addr) => (
                      <SelectItem key={addr.id} value={addr.id}>
                        {addr.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Цвет линии (нет заказов)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    className="w-12 h-10 p-1"
                    value={colorValue(form.link_color_none, DEFAULT_LINK_COLORS.link_color_none)}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, link_color_none: e.target.value }))
                    }
                  />
                  <Input
                    value={form.link_color_none}
                    onChange={(e) => setForm((prev) => ({ ...prev, link_color_none: e.target.value }))}
                    placeholder="#94a3b8"
                  />
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={form.link_skip_none}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, link_skip_none: checked }))
                      }
                    />
                    <span className="text-xs text-slate-500 whitespace-nowrap">Не рисовать</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Цвет линии (1 адрес с заказом)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    className="w-12 h-10 p-1"
                    value={colorValue(form.link_color_one, DEFAULT_LINK_COLORS.link_color_one)}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, link_color_one: e.target.value }))
                    }
                  />
                  <Input
                    value={form.link_color_one}
                    onChange={(e) => setForm((prev) => ({ ...prev, link_color_one: e.target.value }))}
                    placeholder="#f59e0b"
                  />
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={form.link_skip_one}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, link_skip_one: checked }))
                      }
                    />
                    <span className="text-xs text-slate-500 whitespace-nowrap">Не рисовать</span>
                  </div>
                </div>
              </div>
              <div>
                <Label>Цвет линии (оба адреса с заказами)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    className="w-12 h-10 p-1"
                    value={colorValue(form.link_color_both, DEFAULT_LINK_COLORS.link_color_both)}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, link_color_both: e.target.value }))
                    }
                  />
                  <Input
                    value={form.link_color_both}
                    onChange={(e) => setForm((prev) => ({ ...prev, link_color_both: e.target.value }))}
                    placeholder="#22c55e"
                  />
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={form.link_skip_both}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({ ...prev, link_skip_both: checked }))
                      }
                    />
                    <span className="text-xs text-slate-500 whitespace-nowrap">Не рисовать</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Статус</Label>
              <Select
                value={form.status}
                onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Активен</SelectItem>
                  <SelectItem value="deleted">Удалён</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Switch
                checked={form.is_ready}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_ready: checked }))}
              />
              <div>
                <Label className="font-medium">Точка готова</Label>
                <p className="text-xs text-slate-500">
                  Готовые точки отображаются с emoji готовности.
                </p>
              </div>
            </div>
          </div>

          {errors.submit && (
            <p className="text-sm text-red-500">{errors.submit}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={isSaving}>
              {isSaving ? "Сохраняем..." : initialData ? "Сохранить" : "Добавить"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TargetAddressDialog;
